import * as React from 'react';
import S from 's-js';
import * as L from 'leaflet';
import 'leaflet-draw';
import 'leaflet-providers';
import { SuperCriticalComponent, SuperCriticalReference } from 'lib/supercritical';

import { PolygonCoords, LatLong, parseZoneCoordinates } from 'data/models/ZoneCoordinates';

import { WartsilaEnrichmentsMap } from './map';

export class MapView extends SuperCriticalComponent<{ model : WartsilaEnrichmentsMap }> {
    ref : (el : HTMLDivElement | null) => void;

    constructor(props : { model : WartsilaEnrichmentsMap }) {
        super(props);
        this.ref = SuperCriticalReference(this, loadMap);
    }

    srender() {
        return (
            <div className="wartsila-enrichments estimated-route-map" ref={this.ref} />
        );
    }
}

function loadMap({ model: m } : { model : WartsilaEnrichmentsMap }, el : HTMLDivElement) {
    const map = L.map(el, { attributionControl: true });
    S.cleanup(() => map.remove());

    L.tileLayer((L.TileLayer as any).Provider.providers.Esri.url, (L.TileLayer as any).Provider.providers.Esri.variants.WorldImagery.options).addTo(map);

    const
        route = () =>
            m.RelevantQuery() &&
            m.RelevantQuery()!.Result() &&
            m.RelevantQuery()!.Result()!.Model.EstimatedRoute(),
        routePolyline = S(() => {
            const r = route();
            return r && r.length ? L.polyline(r.map(LatLong.toLeaflet), { color: '#FFFFFF', opacity: 0.9 }) : null;
        }),
        sourceLatLng = () => {
            const r = route();
            return r && r.length ? r[0] : null;
        },
        destinationCoords = S(() => {
            const query = m.RelevantQuery();
            if (!query) return null;
            // For now, just going with the zoone coords and zone type from the query entity itself.
            // The alternative is that we could add fallbacks to check the query's Destination nav
            // prop or even go straight to the portcall Itinerary, which might be pre-loaded. But
            // neither of those adds much since we're only initializing the map view if we have a valid
            // estimated route anyway. See the note below.
            return parseZoneCoordinates(query.Model.DestinationZoneCoordinates(), query.Model.DestinationZoneType());
        });

    // Zoom and position map
    S(() => {
        // For now, going with the theory that to position the map, we need an estimated route.
        // Otherwise we could, oh, try to center the view on the destination location, but I
        // found that made things kinda jumpy in the case where we do have an estimated route,
        // and also didn't add much useful info in the case that we don't.
        const polyline = routePolyline();
        if (!polyline) return;
        map.fitBounds(polyline.getBounds().pad(0.3));
    });

    // Draw source marker
    const srcIcon = L.divIcon({
        className: 'map-icon map-icon-src fa fa-map-marker',
        // NOTE: These sizes need to be tuned in coordination with the font-size rule on
        // .map-icon in mapview.less.
        iconSize: L.point(28, 28),
        // Tell Leaflet where the tip of our icon is relative to its top left corner.
        iconAnchor: L.point(14, 25)
    });
    S(() => {
        const src = sourceLatLng();
        if (!src) return;
        const srcMarker = L.marker(LatLong.toLeaflet(src), { icon: srcIcon });
        map.addLayer(srcMarker);
        S.cleanup(() => map.removeLayer(srcMarker));
    });

    // Draw destination marker
    const destIcon = L.divIcon({
        className: 'map-icon map-icon-dest fa fa-times',
        // NOTE: These sizes need to be tuned in coordination with the font-size rule on
        // .map-icon in mapview.less.
        iconSize: L.point(28, 28)
    });
    S(() => {
        const dest = destinationCoords();
        if (!dest) return;

        // We won't draw the destination zone in full detail b/c we expect this
        // map to be too zoomed out for that to be useful. Instead, just
        // place a marker at the center of the destination.
        const center = dest instanceof PolygonCoords ?
            dest.toLeaflet().getBounds().getCenter() :
            dest.toLeaflet().getLatLng();
        const destMarker = L.marker(center, { icon: destIcon });
        map.addLayer(destMarker);
        S.cleanup(() => map.removeLayer(destMarker));
    });

    // Draw route
    S(() => {
        const polyline = routePolyline();
        if (!polyline) return;
        map.addLayer(polyline);
        S.cleanup(() => map.removeLayer(polyline));
    });
}



// WEBPACK FOOTER //
// ./apps/wartsila/wartsilaenrichments/mapview.tsx